import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import DOMPurify from "dompurify";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { 
  ArrowLeft, Heart, Sparkles, MessageCircle, 
  Trash2, RefreshCw, Lightbulb, ExternalLink, User, Database, BookOpen, Edit, X, ImageIcon, Upload, Star, Variable, Eye, EyeOff, Play, Edit2, Clock, HelpCircle, ChevronDown, ChevronUp, Menu, Shield, ListChecks, AlertCircle
} from "lucide-react";
import { Toaster, toast } from "sonner";
import DataSourcesManager from "../components/DataSourcesManager";
import LearningResourcesManager from "../components/LearningResourcesManager";
import UpdatesTab from "../components/relationships/UpdatesTab";
import FamiliarityV2 from "../components/FamiliarityV2";
import TimelineTab from "../components/TimelineTab";
import PrivacyIcon from "../components/PrivacyIcon";
import { getConnectionType, getConnectionColor } from "../utils/connectionTypes";
import { useCoinGate } from "../contexts/CoinGateContext";
import { Progress } from "../components/ui/progress";
import AdminMenu from "../components/AdminMenu";
import MainMenu from "../components/MainMenu";
import CoinBalancePill from "../components/CoinBalancePill";
import NotificationBell from "../components/notifications/NotificationBell";
import {
  REL_TYPE_OPTIONS,
  THEIR_REL_STATUS_OPTIONS,
  findLabel,
} from "../constants/relationshipOptions";

// Backend URL for static file access
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to convert relative static paths to full URLs
const getMediaUrl = (path) => {
  if (!path) return "";
  // If already a full URL or data URL, return as-is
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  // Prepend backend URL for relative paths
  return `${BACKEND_URL}${path}`;
};

// PersonaConnection levels (1-13) - for display only
const PERSONA_CONNECTION_LEVELS = [
  { value: 1, title: "Spark", description: "First awareness and curiosity, something catches your attention." },
  { value: 2, title: "Opening", description: "Initial contact begins; energy, tone, and intent are revealed." },
  { value: 3, title: "Resonance", description: "Conversation flows and mutual interest starts to feel natural." },
  { value: 4, title: "Safety", description: "Trust forms through consistency, respect, and emotional ease." },
  { value: 5, title: "Curiosity", description: "You begin wanting to know more - values, stories, inner worlds." },
  { value: 6, title: "Attraction", description: "Chemistry builds across emotional, mental, and physical levels." },
  { value: 7, title: "Vulnerability", description: "Walls soften; honesty and real feelings are shared." },
  { value: 8, title: "Alignment", description: "Values, desires, and intentions start to line up." },
  { value: 9, title: "Momentum", description: "Connection strengthens through shared time, effort, and intention." },
  { value: 10, title: "Intimacy", description: "Emotional and/or physical closeness deepens meaningfully." },
  { value: 11, title: "Integration", description: "You begin weaving each other into daily life and routines." },
  { value: 12, title: "Commitment", description: "Clear choice is made to prioritize and invest in the connection." },
  { value: 13, title: "Union", description: "A stable, mutual bond rooted in trust, love, and shared direction." },
];

// Full stage policies for the help modal
const STAGE_POLICIES = {
  1: {
    objective: "Make a memorable first impression. Be approachable and authentic to create that initial spark of interest.",
    personaSees: "Strangers - barely know each other",
    style: { warmth: 30, flirt: 10, disclosure: 10 },
    boundaries: ["No personal questions", "Keep it light"],
    allowed: ["Introduce yourself", "Find common ground"],
    disallowed: ["Ask personal questions", "Flirt heavily"]
  },
  2: {
    objective: "Start a genuine conversation. Establish your presence and build connection through engaging dialogue.",
    personaSees: "New acquaintances - few interactions",
    style: { warmth: 40, flirt: 15, disclosure: 15 },
    boundaries: ["Surface-level sharing only"],
    allowed: ["Light humor", "Show interest"],
    disallowed: ["Deep questions", "Plan dates"]
  },
  3: {
    objective: "Find common ground. Discover shared interests and values to deepen the connection naturally.",
    personaSees: "Acquaintances with potential",
    style: { warmth: 50, flirt: 20, disclosure: 20 },
    boundaries: ["Build rapport first"],
    allowed: ["Find shared interests", "Gentle teasing"],
    disallowed: ["Push for dates", "Love bomb"]
  },
  4: {
    objective: "Create emotional safety. Make them feel comfortable opening up without judgment or pressure.",
    personaSees: "Getting to know each other",
    style: { warmth: 55, flirt: 25, disclosure: 25 },
    boundaries: ["Focus on trust building"],
    allowed: ["Validate feelings", "Share a bit about yourself"],
    disallowed: ["Pressure for commitment", "Intense compliments"]
  },
  5: {
    objective: "Cultivate mutual curiosity. Ask meaningful questions and show genuine interest in their world.",
    personaSees: "Building connection",
    style: { warmth: 60, flirt: 30, disclosure: 35 },
    boundaries: ["Balanced curiosity"],
    allowed: ["Ask meaningful questions", "Show genuine interest"],
    disallowed: ["Interrogate", "Rush intimacy"]
  },
  6: {
    objective: "Let chemistry build naturally. Add playful flirting and let attraction develop organically.",
    personaSees: "Clear mutual interest",
    style: { warmth: 65, flirt: 45, disclosure: 40 },
    boundaries: ["Playful but calibrated"],
    allowed: ["Flirt appropriately", "Suggest casual plans"],
    disallowed: ["Come on too strong", "Ignore signals"]
  },
  7: {
    objective: "Share vulnerabilities. Create space for authentic emotional intimacy and deeper truths.",
    personaSees: "Dating or very close",
    style: { warmth: 70, flirt: 50, disclosure: 50 },
    boundaries: ["Vulnerability with consent"],
    allowed: ["Share deeper stories", "Emotional intimacy"],
    disallowed: ["Trauma dump", "Pressure for reciprocation"]
  },
  8: {
    objective: "Explore alignment. Discuss values and life goals to ensure compatible directions.",
    personaSees: "Established deep trust",
    style: { warmth: 75, flirt: 55, disclosure: 55 },
    boundaries: ["Values discussion welcome"],
    allowed: ["Discuss future", "Explore compatibility"],
    disallowed: ["Ultimatums", "Force commitment talk"]
  },
  9: {
    objective: "Build consistent momentum. Keep connection strong through regular, quality interactions.",
    personaSees: "Very close, potential commitment",
    style: { warmth: 80, flirt: 60, disclosure: 60 },
    boundaries: ["Consistent presence expected"],
    allowed: ["Make plans together", "Express appreciation"],
    disallowed: ["Become complacent", "Take for granted"]
  },
  10: {
    objective: "Deepen intimacy. Navigate emotional and physical closeness with care and consent.",
    personaSees: "Deeply committed",
    style: { warmth: 85, flirt: 65, disclosure: 70 },
    boundaries: ["Intimacy with care"],
    allowed: ["Physical affection", "Emotional depth"],
    disallowed: ["Pressure boundaries", "Ignore consent"]
  },
  11: {
    objective: "Integrate lives. Meet important people and share meaningful experiences together.",
    personaSees: "Long-term partnership",
    style: { warmth: 90, flirt: 60, disclosure: 75 },
    boundaries: ["Life integration"],
    allowed: ["Meet friends/family", "Shared experiences"],
    disallowed: ["Rush major decisions", "Lose independence"]
  },
  12: {
    objective: "Establish commitment. Have honest conversations about relationship status and exclusivity.",
    personaSees: "Facing challenges together",
    style: { warmth: 90, flirt: 55, disclosure: 80 },
    boundaries: ["Commitment discussions"],
    allowed: ["Define relationship", "Future planning"],
    disallowed: ["Avoid difficult conversations", "Assume exclusivity"]
  },
  13: {
    objective: "Nurture partnership. Continue growing together while maintaining individual identities.",
    personaSees: "Deep partnership and union",
    style: { warmth: 95, flirt: 50, disclosure: 90 },
    boundaries: ["Deep partnership"],
    allowed: ["Full authenticity", "Long-term planning"],
    disallowed: ["Complacency", "Taking partner for granted"]
  }
};

// Attachment styles with descriptions
const ATTACHMENT_STYLES = [
  { value: "secure", label: "Secure", description: "I am worthy of love, and others are generally reliable." },
  { value: "anxious_preoccupied", label: "Anxious (Preoccupied)", description: "I need closeness to feel safe, but I might be abandoned." },
  { value: "avoidant_dismissive", label: "Avoidant (Dismissive)", description: "I am safest relying on myself; closeness is risky." },
  { value: "fearful_avoidant", label: "Fearful-Avoidant (Disorganized)", description: "I want closeness, but it feels dangerous." },
  { value: "anxious_avoidant", label: "Anxious-Avoidant (Mixed)", description: "I desire connection, but it overwhelms me when it arrives." },
  { value: "disorganized", label: "Disorganized (Trauma-Linked)", description: "Love is unpredictable and unsafe." },
  { value: "earned_secure", label: "Earned Secure", description: "I can create safety and trust through awareness and repair." },
];

// Body type labels
const BODY_TYPE_LABELS = {
  slim: "Slim",
  athletic: "Athletic",
  average: "Average",
  curvy: "Curvy",
  muscular: "Muscular",
  heavyset: "Heavyset"
};

// Dropdown value labels
const DROPDOWN_LABELS = {
  stress_response: { withdraw: "Withdraw", talk_it_out: "Talk it out", distract_myself: "Distract myself", get_irritable: "Get irritable" },
  connection_style: { talking_deeply: "Talking deeply", spending_time: "Spending time together", physical_affection: "Physical affection", shared_activities: "Shared activities" },
  communication_pref_style: { direct: "Direct", gentle: "Gentle", playful: "Playful", reflective: "Reflective" },
  message_length_pref: { short: "Short", medium: "Medium", long: "Long" },
  conflict_style: { avoidant: "Avoidant", collaborative: "Collaborative", assertive: "Assertive", emotional: "Emotional" },
  emotional_openness: { fast: "Fast", slowly: "Slowly", case_by_case: "Case-by-case" },
  needs_consistency: { yes: "Yes", no: "No", sometimes: "Sometimes" },
  relationship_feel_pref: { calm: "Calm", exciting: "Exciting", deep: "Deep", structured: "Structured" },
  social_energy: { introverted: "Introverted", ambivert: "Ambivert", extroverted: "Extroverted" },
  connection_rhythm: { daily: "Daily touchpoints", few_times_week: "A few times a week", spacious: "Spacious" },
  flirting_style: { playful: "Playful", direct: "Direct", subtle: "Subtle", expressive: "Expressive" },
  date_vibe_pref: { cozy: "Cozy", adventurous: "Adventurous", intellectual: "Intellectual", sensual: "Sensual" }
};

// Multi-select value labels
const MULTI_SELECT_LABELS = {
  // Relationship matters
  trust_honesty: "Trust and honesty",
  emotional_safety: "Emotional safety",
  feeling_understood: "Feeling understood and seen",
  mutual_respect: "Mutual respect",
  open_communication: "Open and clear communication",
  talk_through_problems: "Being able to talk through problems",
  feeling_appreciated: "Feeling appreciated and valued",
  shared_values: "Shared values and principles",
  integrity_accountability: "Integrity and accountability",
  growth_awareness: "Growth and self-awareness",
  shared_spiritual: "Shared spiritual values",
  consistent_effort: "Consistent effort and follow-through",
  quality_time: "Quality time and shared experiences",
  reliability_stability: "Reliability and stability",
  independence_space: "Independence and personal space",
  support_goals: "Support for each other's goals",
  healthy_boundaries: "Healthy boundaries",
  // Shutdown triggers
  low_effort_replies: "Low effort or one-word replies",
  talked_over: "Being talked over or not listened to",
  too_many_questions: "Too many questions or long monologues",
  feeling_judged: "Feeling judged or criticised",
  feelings_dismissed: "My feelings being minimised or dismissed",
  pressure_to_move: "Pressure to move faster than comfortable",
  sexual_escalation_early: "Sexual escalation too early",
  dishonesty: "Dishonesty or half-truths",
  inconsistent_behavior: "Inconsistent behaviour or mixed signals",
  overwhelming_intensity: "Intensity that feels overwhelming",
  emotionally_unavailable: "Emotionally unavailable or avoidant behaviour",
  ghosting: "Ghosting or disappearing without explanation",
  excessive_messaging: "Excessive or intrusive messaging",
  disrespectful_controlling: "Disrespectful or controlling behaviour",
  lack_accountability: "Lack of accountability",
  // Attraction qualities
  calm_grounded: "Calm and grounded",
  confident: "Confident (not arrogant)",
  playful_fun: "Playful and fun",
  emotionally_present: "Emotionally present",
  kind_considerate: "Kind and considerate",
  emotionally_open: "Emotionally open and honest",
  secure_themselves: "Secure in themselves",
  clear_direct: "Clear and direct communicator",
  curious_engaging: "Curious and engaging",
  good_listener: "Good listener",
  warm_affectionate: "Warm and affectionate",
  gently_flirtatious: "Gently flirtatious",
  direct_interest: "Direct about their interest"
};

// Zodiac calculation helpers
const getZodiacSign = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  
  if ((month == 3 && day >= 21) || (month == 4 && day <= 19)) return "Aries ♈";
  if ((month == 4 && day >= 20) || (month == 5 && day <= 20)) return "Taurus ♉";
  if ((month == 5 && day >= 21) || (month == 6 && day <= 20)) return "Gemini ♊";
  if ((month == 6 && day >= 21) || (month == 7 && day <= 22)) return "Cancer ♋";
  if ((month == 7 && day >= 23) || (month == 8 && day <= 22)) return "Leo ♌";
  if ((month == 8 && day >= 23) || (month == 9 && day <= 22)) return "Virgo ♍";
  if ((month == 9 && day >= 23) || (month == 10 && day <= 22)) return "Libra ♎";
  if ((month == 10 && day >= 23) || (month == 11 && day <= 21)) return "Scorpio ♏";
  if ((month == 11 && day >= 22) || (month == 12 && day <= 21)) return "Sagittarius ♐";
  if ((month == 12 && day >= 22) || (month == 1 && day <= 19)) return "Capricorn ♑";
  if ((month == 1 && day >= 20) || (month == 2 && day <= 18)) return "Aquarius ♒";
  return "Pisces ♓";
};

// Chinese New Year dates (approximate - falls between Jan 21 and Feb 20)
// This is a simplified lookup for common years. For full accuracy, a lunar calendar library would be needed.
const CHINESE_NEW_YEAR_DATES = {
  1940: "02-08", 1941: "01-27", 1942: "02-15", 1943: "02-05", 1944: "01-25",
  1945: "02-13", 1946: "02-02", 1947: "01-22", 1948: "02-10", 1949: "01-29",
  1950: "02-17", 1951: "02-06", 1952: "01-27", 1953: "02-14", 1954: "02-03",
  1955: "01-24", 1956: "02-12", 1957: "01-31", 1958: "02-18", 1959: "02-08",
  1960: "01-28", 1961: "02-15", 1962: "02-05", 1963: "01-25", 1964: "02-13",
  1965: "02-02", 1966: "01-21", 1967: "02-09", 1968: "01-30", 1969: "02-17",
  1970: "02-06", 1971: "01-27", 1972: "02-15", 1973: "02-03", 1974: "01-23",
  1975: "02-11", 1976: "01-31", 1977: "02-18", 1978: "02-07", 1979: "01-28",
  1980: "02-16", 1981: "02-05", 1982: "01-25", 1983: "02-13", 1984: "02-02",
  1985: "02-20", 1986: "02-09", 1987: "01-29", 1988: "02-17", 1989: "02-06",
  1990: "01-27", 1991: "02-15", 1992: "02-04", 1993: "01-23", 1994: "02-10",
  1995: "01-31", 1996: "02-19", 1997: "02-07", 1998: "01-28", 1999: "02-16",
  2000: "02-05", 2001: "01-24", 2002: "02-12", 2003: "02-01", 2004: "01-22",
  2005: "02-09", 2006: "01-29", 2007: "02-18", 2008: "02-07", 2009: "01-26",
  2010: "02-14", 2011: "02-03", 2012: "01-23", 2013: "02-10", 2014: "01-31",
  2015: "02-19", 2016: "02-08", 2017: "01-28", 2018: "02-16", 2019: "02-05",
  2020: "01-25", 2021: "02-12", 2022: "02-01", 2023: "01-22", 2024: "02-10",
  2025: "01-29", 2026: "02-17", 2027: "02-06", 2028: "01-26", 2029: "02-13",
  2030: "02-03"
};

const getChineseZodiac = (dateString) => {
  if (!dateString) return null;
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null; // Invalid date
    
    let year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    
    // Check if date is before Chinese New Year - if so, use previous year's zodiac
    const cnyDate = CHINESE_NEW_YEAR_DATES[year];
    if (cnyDate) {
      const [cnyMonth, cnyDay] = cnyDate.split("-").map(Number);
      if (month < cnyMonth || (month === cnyMonth && day < cnyDay)) {
        year = year - 1;
      }
    } else {
      // Fallback: if year not in lookup, estimate CNY as Feb 5
      if (month === 1 || (month === 2 && day < 5)) {
        year = year - 1;
      }
    }
    
    // Animals in the 12-year cycle (starting from Rat at year 4 AD)
    const animals = [
      { name: "Rat", emoji: "🐀" },
      { name: "Ox", emoji: "🐂" },
      { name: "Tiger", emoji: "🐅" },
      { name: "Rabbit", emoji: "🐇" },
      { name: "Dragon", emoji: "🐉" },
      { name: "Snake", emoji: "🐍" },
      { name: "Horse", emoji: "🐴" },
      { name: "Goat", emoji: "🐐" },
      { name: "Monkey", emoji: "🐵" },
      { name: "Rooster", emoji: "🐓" },
      { name: "Dog", emoji: "🐕" },
      { name: "Pig", emoji: "🐖" }
    ];
    
    // Elements in the 10-year cycle (each element covers 2 years)
    // Years ending in: 0,1=Metal, 2,3=Water, 4,5=Wood, 6,7=Fire, 8,9=Earth
    const elements = ["Metal", "Water", "Wood", "Fire", "Earth"];
    const lastDigit = year % 10;
    // Adjust for the cycle: 0,1->Metal, 2,3->Water, etc.
    const elementMap = { 0: 0, 1: 0, 2: 1, 3: 1, 4: 2, 5: 2, 6: 3, 7: 3, 8: 4, 9: 4 };
    const element = elements[elementMap[lastDigit]] || "Unknown";
    
    // Calculate animal (year 4 AD was a Rat year)
    let animalIndex = (year - 4) % 12;
    if (animalIndex < 0) animalIndex += 12;
    const animal = animals[animalIndex];
    
    if (!animal) return null;
    
    return `${element} ${animal.name} ${animal.emoji}`;
  } catch (error) {
    console.error("Error calculating Chinese zodiac:", error);
    return null;
  }
};

// Section header component with edit button. `onEdit` is the parent's
// coin-gated navigator (see `gatedEdit` inside MatchDetail) — it fires the
// "Out of Coins" modal when the balance is at/below zero and only navigates
// once the user has coins. Passing a callback (rather than `navigate`) keeps
// the gating logic in the parent where `useCoinGate()` is available.
const SectionHeader = ({ title, sectionKey, matchId, onEdit }) => (
  <div className="flex items-center justify-between mb-4">
    <h3 className="font-cinzel text-lg text-white">{title}</h3>
    <Button
      variant="ghost"
      size="sm"
      onClick={() => onEdit(`/relationships/${matchId}/edit-section?section=${sectionKey}`)}
      className="text-white/40 hover:text-divine-gold hover:bg-divine-gold/10"
      title={`Edit ${title}`}
      data-testid={`edit-section-${sectionKey}`}
    >
      <Edit2 className="w-4 h-4" />
    </Button>
  </div>
);

// Field display component with optional debug field name and privacy icon
const FieldDisplay = ({ 
  label, 
  fieldName, 
  value, 
  showDebug, 
  className = "",
  matchId,
  matchName,
  privacyLevel,
  onPrivacyChange,
  showPrivacy = false,
  descriptor = null
}) => (
  <div className={className}>
    <label className="text-white/50 text-xs uppercase tracking-wider flex items-center gap-1.5">
      {showPrivacy && matchId && fieldName && (
        <PrivacyIcon
          fieldKey={fieldName}
          privacyLevel={privacyLevel || 0}
          matchId={matchId}
          matchName={matchName}
          onPrivacyChange={onPrivacyChange}
          size="sm"
        />
      )}
      {label}
      {showDebug && fieldName && (
        <span className="ml-2 text-divine-gold font-mono bg-divine-gold/10 px-1 rounded normal-case">
          {fieldName}
        </span>
      )}
    </label>
    <p className="text-white/80 mt-1 whitespace-pre-wrap">{value || "-"}</p>
    {descriptor && (
      <p className="text-white/40 text-xs mt-0.5 italic">{descriptor}</p>
    )}
  </div>
);

// Multi-select display component
const MultiSelectDisplay = ({ 
  label, 
  fieldName, 
  values, 
  showDebug,
  matchId,
  matchName,
  privacyLevel,
  onPrivacyChange,
  showPrivacy = false
}) => {
  // Ensure values is an array
  const valuesArray = Array.isArray(values) ? values : (values ? [values] : []);
  
  if (!valuesArray || valuesArray.length === 0) {
    return (
      <FieldDisplay 
        label={label} 
        fieldName={fieldName} 
        value="-" 
        showDebug={showDebug}
        matchId={matchId}
        matchName={matchName}
        privacyLevel={privacyLevel}
        onPrivacyChange={onPrivacyChange}
        showPrivacy={showPrivacy}
      />
    );
  }
  
  return (
    <div>
      <label className="text-white/50 text-xs uppercase tracking-wider flex items-center gap-1.5">
        {showPrivacy && matchId && fieldName && (
          <PrivacyIcon
            fieldKey={fieldName}
            privacyLevel={privacyLevel || 0}
            matchId={matchId}
            matchName={matchName}
            onPrivacyChange={onPrivacyChange}
            size="sm"
          />
        )}
        {label}
        {showDebug && fieldName && (
          <span className="ml-2 text-divine-gold font-mono bg-divine-gold/10 px-1 rounded normal-case">
            {fieldName}
          </span>
        )}
      </label>
      <div className="flex flex-wrap gap-2 mt-2">
        {valuesArray.map((v) => (
          <span key={v} className="px-2 py-1 bg-divine-gold/20 text-divine-gold text-sm rounded">
            {MULTI_SELECT_LABELS[v] || String(v).replace(/_/g, " ")}
          </span>
        ))}
      </div>
    </div>
  );
};

const MatchDetail = () => {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [match, setMatch] = useState(null);
  const { requireCoins } = useCoinGate();
  const gatedEdit = async (path) => { if (await requireCoins()) navigate(path); };
  // For the user's own self-persona we hide a handful of relational fields
  // (persona_connection, relationship_status, history_together, and the two
  // feelings columns) — they describe a relationship between the user and
  // someone else, which is meaningless against the user themselves. Existing
  // values stay in DB; this is a UI-only hide.
  const isUserPersona = match?.match_type === "user_persona";
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [starters, setStarters] = useState("");
  const [loadingStarters, setLoadingStarters] = useState(false);
  
  // Support both location.state and URL query params for activeTab
  const searchParams = new URLSearchParams(location.search);
  const tabFromQuery = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromQuery || location.state?.activeTab || "overview");
  // Bump this on every "learning" tab click so <LearningResourcesManager />
  // remounts and re-fetches its category list (refresh-on-open behaviour).
  const [learningRefreshKey, setLearningRefreshKey] = useState(0);
  const handleTabChange = (nextTab) => {
    setActiveTab(nextTab);
    if (nextTab === "learning") setLearningRefreshKey((k) => k + 1);
  };
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [roleplayModalOpen, setRoleplayModalOpen] = useState(false);
  // User self-persona completion — needed to gate compatibility analysis
  const [userProfileCompletion, setUserProfileCompletion] = useState(0);
  const [insufficientDataOpen, setInsufficientDataOpen] = useState(false);
  
  // Admin debug mode
  const [showFieldNames, setShowFieldNames] = useState(false);
  const isAdmin = user?.role === "admin";
  
  // Images state
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImageForView, setSelectedImageForView] = useState(null);
  // Track image IDs whose <img> fired onError. React state is the source
  // of truth here — do NOT mutate parentElement.innerHTML from onError
  // handlers, that fights React reconciliation and produces an infinite
  // onError loop on prod when a media URL 404s (see 2026-07-13 fix).
  const [brokenMediaIds, setBrokenMediaIds] = useState(() => new Set());
  const markMediaBroken = (imageId) => {
    setBrokenMediaIds((prev) => {
      if (prev.has(imageId)) return prev;
      const next = new Set(prev);
      next.add(imageId);
      return next;
    });
  };
  
  // Privacy settings state
  const [privacySettings, setPrivacySettings] = useState({});
  const [showPrivacyIcons, setShowPrivacyIcons] = useState(false);
  
  // Connection stages help modal
  const [showStagesHelp, setShowStagesHelp] = useState(false);
  const [expandedStage, setExpandedStage] = useState(null);
  
  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Top compatibility match (only fetched when viewing the user's own persona)
  const [topCompat, setTopCompat] = useState(null);
  const [topCompatLoading, setTopCompatLoading] = useState(false);
  
  // Tab items for mobile menu. `adminOnly` items are filtered out entirely
  // for non-admin users on both mobile and desktop (see the TabsList render
  // below which uses the same isAdmin gate).
  const tabItems = [
    { value: "overview", label: "Overview", icon: User },
    { value: "memory", label: "Memory", icon: Heart, adminOnly: true },
    { value: "updates", label: "Updates", icon: ListChecks },
    { value: "timeline", label: "Timeline", icon: Clock },
    { value: "images", label: "Media", icon: ImageIcon },
    { value: "data-sources", label: "Data", icon: Database },
    { value: "learning", label: "Learning", icon: BookOpen },
  ].filter((item) => !item.adminOnly || isAdmin);

  useEffect(() => {
    fetchMatch();
    fetchPrivacySettings();
    // Fetch the user's own persona so we can gate the compatibility
    // analysis button on both profiles being >75% complete. The endpoint
    // is /matches/user-persona (not /matches/me — that path collides
    // with /matches/{match_id}).
    authAxios
      .get("/matches/user-persona")
      .then((r) => setUserProfileCompletion(r?.data?.profile_completion ?? 0))
      .catch(() => setUserProfileCompletion(0));
  }, [matchId]);

  const fetchPrivacySettings = async () => {
    try {
      const response = await authAxios.get(`/privacy-settings/${matchId}`);
      setPrivacySettings(response.data.field_privacy || {});
    } catch (error) {
      console.error("Error fetching privacy settings:", error);
    }
  };

  const handlePrivacyChange = (fieldKey, newLevel) => {
    setPrivacySettings(prev => ({
      ...prev,
      [fieldKey]: newLevel
    }));
  };

  const fetchMatch = async () => {
    try {
      const response = await authAxios.get(`/matches/${matchId}`);
      setMatch(response.data);
    } catch (error) {
      toast.error("Relationship not found");
      navigate("/relationships");
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!(await requireCoins())) return;
    setAnalyzing(true);
    try {
      const response = await authAxios.post(`/ai/analyze-compatibility?match_id=${matchId}`, {});
      toast.success("Compatibility analyzed by Eros!");
      fetchMatch();
    } catch (error) {
      toast.error("Analysis failed. Please try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGetStarters = async () => {
    setLoadingStarters(true);
    try {
      const response = await authAxios.post("/ai/conversation-starters", { match_id: matchId });
      setStarters(response.data.starters);
    } catch (error) {
      toast.error("Failed to generate starters");
    } finally {
      setLoadingStarters(false);
    }
  };

  // Image management functions
  const fetchImages = async () => {
    try {
      setLoadingImages(true);
      const response = await authAxios.get(`/matches/${matchId}/images`);
      setImages(response.data.images || []);
      // Clear the broken-media set on refetch so previously failed URLs
      // get a fresh chance (e.g. after a re-upload or when the object
      // storage eventually catches up).
      setBrokenMediaIds(new Set());
    } catch (error) {
      console.error("Error fetching images:", error);
    } finally {
      setLoadingImages(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");
    
    if (!isImage && !isVideo) {
      toast.error("Please select an image or video file");
      return;
    }

    // Size limits: 16MB for both images and videos
    const maxSize = 16 * 1024 * 1024; // 16MB
    if (file.size > maxSize) {
      toast.error("File must be less than 16MB");
      return;
    }

    try {
      setUploadingImage(true);
      
      if (isVideo) {
        // For videos, extract first frame as thumbnail
        const videoUrl = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.src = videoUrl;
        video.crossOrigin = 'anonymous';
        video.muted = true;
        video.preload = 'metadata';
        
        video.onloadedmetadata = async () => {
          try {
            // Seek to absolute first frame (0 seconds)
            video.currentTime = 0;
          } catch (err) {
            console.error("Error seeking video:", err);
            // Fallback: try 0.1 seconds if 0 fails
            video.currentTime = 0.1;
          }
        };
        
        video.onseeked = async () => {
          try {
            // Create canvas to capture frame
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth || 640;
            canvas.height = video.videoHeight || 480;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            
            // Get thumbnail as base64
            const thumbnailData = canvas.toDataURL('image/jpeg', 0.8);
            
            // Read video as base64
            const reader = new FileReader();
            reader.onloadend = async () => {
              try {
                const videoData = reader.result;
                await authAxios.post(`/matches/${matchId}/media`, {
                  media_data: videoData,
                  thumbnail_data: thumbnailData,
                  media_type: "video",
                  caption: ""
                });
                toast.success("Video uploaded successfully");
                fetchImages();
                fetchMatch();
              } catch (error) {
                console.error("Upload error:", error);
                const errMsg = error.response?.data?.detail || "Failed to upload video";
                toast.error(errMsg);
              } finally {
                setUploadingImage(false);
                URL.revokeObjectURL(videoUrl);
              }
            };
            reader.readAsDataURL(file);
          } catch (error) {
            console.error("Error capturing video frame:", error);
            toast.error("Failed to process video");
            setUploadingImage(false);
            URL.revokeObjectURL(videoUrl);
          }
        };
        
        video.onerror = () => {
          toast.error("Failed to load video");
          setUploadingImage(false);
          URL.revokeObjectURL(videoUrl);
        };
        
        video.load();
      } else {
        // Handle image upload (existing logic)
        const reader = new FileReader();
        reader.onloadend = async () => {
          try {
            const base64Data = reader.result;
            await authAxios.post(`/matches/${matchId}/images`, {
              image_data: base64Data,
              caption: ""
            });
            toast.success("Image uploaded successfully");
            fetchImages();
            fetchMatch();
          } catch (error) {
            console.error("Upload error:", error);
            toast.error("Failed to upload image");
          } finally {
            setUploadingImage(false);
          }
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      console.error("Error reading file:", error);
      toast.error("Failed to read file");
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm("Delete this image?")) return;
    
    try {
      await authAxios.delete(`/matches/${matchId}/images/${imageId}`);
      toast.success("Image deleted");
      fetchImages();
      fetchMatch();
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  const handleSetPrimaryImage = async (imageId) => {
    try {
      await authAxios.put(`/matches/${matchId}/images/${imageId}/primary`);
      toast.success("Primary image updated");
      fetchImages();
      fetchMatch();
    } catch (error) {
      toast.error("Failed to set primary image");
    }
  };

  const handleToggleVideoInChat = async (imageId) => {
    try {
      const response = await authAxios.put(`/matches/${matchId}/images/${imageId}/chat-toggle`);
      toast.success(response.data.message);
      fetchImages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to toggle video");
    }
  };

  useEffect(() => {
    // Always fetch the images list once per matchId (not tab-gated) so we
    // can fall back to a primary media library photo as the profile picture
    // on every tab, even before the user visits the Images tab.
    if (matchId) fetchImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  // When this profile is the user's own persona, surface their highest-scoring
  // compatibility relationship in place of the (meaningless) self-compatibility
  // card. Fetch the light /matches list, pick the top match_persona by score,
  // then hydrate its analysis.summary via the detail endpoint.
  useEffect(() => {
    if (!isUserPersona) {
      setTopCompat(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setTopCompatLoading(true);
        const listRes = await authAxios.get("/matches");
        const scored = (listRes.data || []).filter(
          (m) =>
            m.match_type !== "user_persona" &&
            m.match_id !== matchId &&
            typeof m.compatibility_score === "number"
        );
        if (scored.length === 0) {
          if (!cancelled) setTopCompat(null);
          return;
        }
        scored.sort(
          (a, b) => (b.compatibility_score || 0) - (a.compatibility_score || 0)
        );
        const top = scored[0];
        // Fetch detail for analysis.summary (not returned by the light list)
        const detailRes = await authAxios.get(`/matches/${top.match_id}`);
        if (cancelled) return;
        setTopCompat({
          match_id: top.match_id,
          name: top.name,
          compatibility_score: top.compatibility_score,
          thumbnail_url: top.thumbnail_url,
          picture_url: detailRes.data?.picture_url || top.picture_url,
          picture: detailRes.data?.picture,
          summary: detailRes.data?.analysis?.summary || "",
        });
      } catch (err) {
        console.error("Error fetching top compatibility:", err);
        if (!cancelled) setTopCompat(null);
      } finally {
        if (!cancelled) setTopCompatLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isUserPersona, matchId]);

  const handleDelete = async () => {
    if (!deleteConfirmed) {
      toast.error(`Please confirm YES to delete ${match?.name || 'this relationship'}`);
      return;
    }
    
    setDeleting(true);
    try {
      await authAxios.delete(`/matches/${matchId}?confirmed=true`);
      toast.success(`${match?.name || 'Relationship'} and all associated data permanently deleted`);
      setShowDeleteModal(false);
      navigate("/relationships");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete relationship");
      setDeleting(false);
    }
  };
  
  const openDeleteModal = () => {
    setDeleteConfirmed(false);
    setShowDeleteModal(true);
  };

  // Helper to get dropdown label
  const getDropdownLabel = (field, value) => {
    if (!value) return "-";
    return DROPDOWN_LABELS[field]?.[value] || value.replace(/_/g, " ");
  };

  // Derived profile picture with media-library fallback.
  //
  // Priority:
  //   1. `match.picture_url` / `match.picture` (explicitly set)
  //   2. Primary non-video item from the Images tab (is_primary flag)
  //   3. First non-video item (any photo)
  //
  // Videos are excluded from the fallback because the avatar renders as a
  // still <img> — a video URL would fail to load and leave a blank circle.
  const displayPicture = useMemo(() => {
    const explicit = match?.picture_url || match?.picture;
    if (explicit) return explicit;
    if (!Array.isArray(images) || images.length === 0) return "";
    const isPhoto = (im) => (im?.media_type || "image") !== "video";
    const primaryPhoto = images.find((im) => im?.is_primary && isPhoto(im));
    const anyPhoto = primaryPhoto || images.find(isPhoto);
    if (!anyPhoto) return "";
    return getMediaUrl(anyPhoto.src || anyPhoto.file_path || anyPhoto.data || "");
  }, [match?.picture_url, match?.picture, images]);

  if (loading) {
    return (
      <div className="min-h-screen bg-divine-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-divine-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-divine-black">
      <Toaster position="top-center" richColors />

      {/* Insufficient Data for Compatibility Modal */}
      <Dialog open={insufficientDataOpen} onOpenChange={setInsufficientDataOpen}>
        <DialogContent
          className="bg-divine-black border border-amber-500/30 max-w-md"
          data-testid="insufficient-data-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-cinzel text-xl text-amber-400 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              More Profile Data Needed
            </DialogTitle>
          </DialogHeader>

          <p className="text-white/70 text-sm">
            Add more data to the relationship profile and your profile to see compatibility.
          </p>

          <div className="space-y-4 mt-2">
            {/* Match profile progress */}
            {(() => {
              const mc = match?.profile_completion ?? 0;
              const done = mc >= 90;
              return (
                <div data-testid="insufficient-data-match-progress">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white/80">
                      {match?.name || "Relationship"}'s Profile
                    </span>
                    <span className={`text-xs font-mono ${done ? "text-green-400" : "text-amber-400"}`}>
                      {mc}% / 90%
                    </span>
                  </div>
                  <div className="relative h-2 w-full overflow-hidden rounded-full bg-white/10">
                    <div
                      className={`h-full transition-all ${done ? "bg-green-500" : "bg-amber-500"}`}
                      style={{ width: `${mc}%` }}
                    />
                  </div>
                </div>
              );
            })()}

            {/* User profile progress */}
            {(() => {
              const uc = userProfileCompletion;
              const done = uc >= 90;
              return (
                <div data-testid="insufficient-data-user-progress">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white/80">Your Profile</span>
                    <span className={`text-xs font-mono ${done ? "text-green-400" : "text-amber-400"}`}>
                      {uc}% / 90%
                    </span>
                  </div>
                  <div className="relative h-2 w-full overflow-hidden rounded-full bg-white/10">
                    <div
                      className={`h-full transition-all ${done ? "bg-green-500" : "bg-amber-500"}`}
                      style={{ width: `${uc}%` }}
                    />
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="mt-2 rounded-lg border border-divine-gold/20 bg-divine-gold/5 px-3 py-2.5 text-xs text-white/70 leading-relaxed flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-divine-gold flex-shrink-0 mt-0.5" />
            <span>
              Agent Eros will automatically add profile data through your ongoing sessions with{" "}
              <em className="text-divine-gold not-italic font-medium">{match?.name || "this relationship"}</em>.
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mt-2">
            <Button
              onClick={() => {
                setInsufficientDataOpen(false);
                navigate(`/relationships/${matchId}/edit`);
              }}
              className="flex-1 bg-divine-gold hover:bg-divine-gold/90 text-black"
              data-testid="insufficient-data-edit-relationship"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Relationship
            </Button>
            <Button
              onClick={() => {
                setInsufficientDataOpen(false);
                navigate("/profile?tab=my-persona");
              }}
              variant="outline"
              className="flex-1 border-divine-gold text-divine-gold hover:bg-divine-gold/10"
              data-testid="insufficient-data-edit-profile"
            >
              <User className="w-4 h-4 mr-2" />
              Edit My Profile
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Image Modal */}
      <Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent className="bg-transparent border-none shadow-none max-w-4xl">
          <button
            onClick={() => setImageModalOpen(false)}
            className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white z-50"
          >
            <X className="w-6 h-6" />
          </button>
          {displayPicture && (
            <img
              src={displayPicture}
              alt={match.name}
              className="max-w-full max-h-[80vh] mx-auto rounded-lg object-contain"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Image View Modal for gallery */}
      <Dialog open={!!selectedImageForView} onOpenChange={() => setSelectedImageForView(null)}>
        <DialogContent className="bg-transparent border-none shadow-none max-w-4xl [&>button:last-child]:hidden">
          <button
            onClick={() => setSelectedImageForView(null)}
            className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white z-50"
            data-testid="image-viewer-close-btn"
          >
            <X className="w-6 h-6" />
          </button>
          {selectedImageForView && (() => {
            // Support both old (data) and new (src/mediaSrc) formats
            // mediaSrc is already resolved with getMediaUrl in the grid
            const src = selectedImageForView.mediaSrc || getMediaUrl(selectedImageForView.src || selectedImageForView.data || "");
            const isVideo = selectedImageForView.media_type === "video" || src.includes("video");
            
            return isVideo ? (
              <video
                src={src}
                controls
                autoPlay
                className="max-w-full max-h-[80vh] mx-auto rounded-lg"
              >
                Your browser does not support video playback.
              </video>
            ) : (
              <img
                src={src}
                alt={selectedImageForView.caption || `Photo of ${match?.name}`}
                className="max-w-full max-h-[80vh] mx-auto rounded-lg object-contain"
              />
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Stages Help Modal */}
      <Dialog open={showStagesHelp} onOpenChange={setShowStagesHelp}>
        <DialogContent className="bg-divine-black border border-divine-gold/20 max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-cinzel text-xl text-divine-gold">
              13 Stages of Persona Connection
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {PERSONA_CONNECTION_LEVELS.map((level) => {
              const policy = STAGE_POLICIES[level.value];
              const isExpanded = expandedStage === level.value;
              
              return (
                <div key={level.value} className="border border-white/10 rounded-lg p-4">
                  <button
                    onClick={() => setExpandedStage(isExpanded ? null : level.value)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <div>
                      <h3 className="font-cinzel text-lg text-white">
                        L{level.value}: {level.title}
                      </h3>
                      <p className="text-white/60 text-sm mt-1">{level.description}</p>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-divine-gold" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-divine-gold" />
                    )}
                  </button>
                  
                  {isExpanded && policy && (
                    <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                      <div>
                        <h4 className="text-divine-gold font-semibold text-sm">Objective</h4>
                        <p className="text-white/80 text-sm">{policy.objective}</p>
                      </div>
                      
                      <div>
                        <h4 className="text-divine-gold font-semibold text-sm">How Persona Sees You</h4>
                        <p className="text-white/80 text-sm">{policy.personaSees}</p>
                      </div>
                      
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <h4 className="text-divine-gold font-semibold text-sm">Style</h4>
                          <div className="text-white/80 text-sm space-y-1">
                            <div>Warmth: {policy.style.warmth}%</div>
                            <div>Flirt: {policy.style.flirt}%</div>
                            <div>Disclosure: {policy.style.disclosure}%</div>
                          </div>
                        </div>
                        
                        <div>
                          <h4 className="text-divine-gold font-semibold text-sm">Allowed</h4>
                          <ul className="text-white/80 text-sm space-y-1">
                            {policy.allowed?.map((item, idx) => (
                              <li key={idx}>• {item}</li>
                            ))}
                          </ul>
                        </div>
                        
                        <div>
                          <h4 className="text-divine-gold font-semibold text-sm">Not Allowed</h4>
                          <ul className="text-white/80 text-sm space-y-1">
                            {policy.disallowed?.map((item, idx) => (
                              <li key={idx}>• {item}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      
                      {policy.boundaries && (
                        <div>
                          <h4 className="text-divine-gold font-semibold text-sm">Boundaries</h4>
                          <ul className="text-white/80 text-sm space-y-1">
                            {policy.boundaries.map((boundary, idx) => (
                              <li key={idx}>• {boundary}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={(open) => {
        if (!deleting) {
          setShowDeleteModal(open);
          if (!open) setDeleteConfirmed(false);
        }
      }}>
        <DialogContent className="bg-divine-black border border-red-500/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-cinzel text-xl text-red-500 flex items-center gap-2">
              <Trash2 className="w-6 h-6" />
              DELETE WARNING
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-white/90">
              This action <span className="text-red-400 font-bold">cannot be undone</span> and will permanently delete:
            </p>
            
            <ul className="text-white/70 text-sm space-y-1 ml-4">
              <li>• All profile data for <span className="text-divine-gold font-semibold">{match?.name}</span></li>
              <li>• All chat sessions and messages</li>
              <li>• All memories and relationship history</li>
              <li>• All images and media files</li>
              <li>• All generated data (style cards, ref sheets, etc.)</li>
              <li>• Timeline events and coaching data</li>
            </ul>
            
            <div className="border border-white/20 rounded-lg p-4 bg-white/5">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteConfirmed}
                  onChange={(e) => setDeleteConfirmed(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-white/30 bg-transparent checked:bg-red-500 checked:border-red-500 focus:ring-red-500"
                  data-testid="delete-confirm-checkbox"
                />
                <span className="text-white">
                  YES - I confirm I want to permanently delete <span className="text-divine-gold font-semibold">{match?.name}</span> and all associated data
                </span>
              </label>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmed(false);
                }}
                disabled={deleting}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleting}
                className={`flex-1 ${
                  deleteConfirmed 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : 'bg-red-600/30 text-white/50 cursor-not-allowed'
                }`}
                data-testid="delete-confirm-btn"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    DELETE
                  </>
                )}
              </Button>
            </div>
            
            {!deleteConfirmed && (
              <p className="text-red-400/80 text-sm text-center">
                Please confirm YES to delete {match?.name}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Header */}
      <header className="border-b border-white/10 bg-divine-black-light sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {/* Back arrow — routes to the previous page in browser history,
                falling back to /relationships if there's nothing to pop. */}
            <Button
              variant="ghost"
              onClick={() => {
                if (window.history.length > 1) navigate(-1);
                else navigate("/relationships");
              }}
              className="text-white/60 hover:text-white p-2 flex-shrink-0"
              data-testid="match-header-back-btn"
              title="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            {/* Match Avatar — falls back to Agent Eros logo when no image */}
            <div className="w-10 h-10 rounded-full bg-divine-gold/20 flex items-center justify-center overflow-hidden border-2 border-divine-gold/30 flex-shrink-0">
              {(match?.thumbnail_url || displayPicture) ? (
                <img
                  src={match?.thumbnail_url || displayPicture}
                  alt={match?.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <img
                  src="https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png"
                  alt="Agent Eros"
                  className="w-6 h-6 object-contain"
                />
              )}
            </div>
            <h1 className="font-cinzel text-lg text-white truncate">{match?.name}</h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Edit / Delete — desktop only, mobile users have them in the tab scroller */}
            <Button
              variant="ghost"
              onClick={() => gatedEdit(`/relationships/${matchId}/edit`)}
              className="hidden md:inline-flex text-divine-gold hover:bg-divine-gold/10"
              data-testid="header-edit-btn"
              title="Edit relationship"
            >
              <Edit className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              onClick={openDeleteModal}
              className="hidden md:inline-flex text-divine-red hover:bg-divine-red/10"
              data-testid="header-delete-btn"
              title="Delete relationship"
            >
              <Trash2 className="w-5 h-5" />
            </Button>
            <CoinBalancePill />
            <NotificationBell />
            <AdminMenu />
            <MainMenu />
          </div>
        </div>
      </header>

      {/* Tabs */}
      <main className="max-w-6xl mx-auto px-6 pt-0.5 md:pt-8 pb-8">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="sticky top-[60px] z-40 flex md:grid w-auto md:w-full md:max-w-5xl md:grid-cols-7 md:justify-center justify-start bg-divine-charcoal border-y border-divine-gold/20 overflow-x-auto md:overflow-visible whitespace-nowrap rounded-none -mx-6 mb-3 pl-4 pr-4 md:mx-0 md:pl-1 md:pr-1 md:rounded-md md:border md:sticky md:top-[66px] touch-pan-x overscroll-x-contain [will-change:transform]">
            <TabsTrigger 
              value="overview"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
            >
              <User className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger 
                value="memory"
                className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
                data-testid="memory-tab-trigger"
              >
                <Heart className="h-4 w-4 mr-2" />
                Memory
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="updates"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
              data-testid="updates-tab-trigger"
            >
              <ListChecks className="h-4 w-4 mr-2" />
              Updates
            </TabsTrigger>
            <TabsTrigger 
              value="timeline"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
              data-testid="timeline-tab-trigger"
            >
              <Clock className="h-4 w-4 mr-2" />
              Timeline
            </TabsTrigger>
            <TabsTrigger 
              value="images"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
            >
              <ImageIcon className="h-4 w-4 mr-2" />
              Media
            </TabsTrigger>
            <TabsTrigger 
              value="data-sources"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
            >
              <Database className="h-4 w-4 mr-2" />
              Data
            </TabsTrigger>
            <TabsTrigger 
              value="learning"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
            >
              <BookOpen className="h-4 w-4 mr-2" />
              Learning
            </TabsTrigger>
            <button
              type="button"
              onClick={() => gatedEdit(`/relationships/${matchId}/edit`)}
              className="md:hidden inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm text-divine-gold hover:bg-divine-gold/10 rounded-md whitespace-nowrap"
              data-testid="submenu-edit-btn"
            >
              <Edit className="h-4 w-4" />
              Edit
            </button>
            <button
              type="button"
              onClick={openDeleteModal}
              className="md:hidden inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm text-divine-red hover:bg-divine-red/10 rounded-md whitespace-nowrap"
              data-testid="submenu-delete-btn"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            <div className="space-y-6">
              {/* Avatar + Compatibility Score Row */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Avatar Section */}
                <div className="glass-gold rounded-xl p-6">
                  <div className="flex items-center gap-6">
                    <div 
                      className={`relative w-24 h-24 flex-shrink-0 ${displayPicture ? 'cursor-pointer group' : ''}`}
                      onClick={() => displayPicture && setImageModalOpen(true)}
                      data-testid="relationship-image"
                    >
                      {displayPicture ? (
                        <>
                          <img 
                            src={displayPicture} 
                            alt={match.name}
                            className="w-full h-full rounded-full object-cover transition-transform group-hover:scale-105"
                          />
                          <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <span className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">View</span>
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full rounded-full bg-divine-gold/20 flex items-center justify-center">
                          <span className="text-divine-gold font-cinzel text-4xl">{match?.name?.[0]}</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <h2 className="font-cinzel text-2xl text-white">{match?.name}</h2>
                      {!isUserPersona && match?.relationship_status && (
                        <p className="text-white/60 text-sm mt-1 capitalize flex items-center gap-2">
                          <Heart className="w-4 h-4" />
                          {match.relationship_status.replace(/_/g, " ")}
                        </p>
                      )}
                      {(match?.date_of_birth || match?.zodiac_sign || match?.chinese_zodiac) && (
                        <div className="mt-2 space-y-0.5">
                          <p className="text-divine-gold text-sm">{match?.zodiac_sign || getZodiacSign(match?.date_of_birth)}</p>
                          <p className="text-white/60 text-xs">{match?.chinese_zodiac || getChineseZodiac(match?.date_of_birth)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Compatibility Score / Top Compatibility (user_persona) */}
                <div className="glass rounded-xl p-6">
                  {isUserPersona ? (
                    topCompatLoading ? (
                      <div className="text-center py-4" data-testid="top-compat-loading">
                        <RefreshCw className="w-8 h-8 text-divine-gold/40 mx-auto mb-2 animate-spin" />
                        <p className="text-white/60 text-sm">Finding your top match...</p>
                      </div>
                    ) : topCompat ? (
                      <div
                        className="cursor-pointer group"
                        onClick={() =>
                          navigate(`/relationships/${topCompat.match_id}/compatibility`)
                        }
                        data-testid="top-compat-card"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="font-cinzel text-lg text-white">Top Compatibility</h3>
                            <span
                              className={`text-sm font-semibold ${getConnectionColor(
                                topCompat.compatibility_score
                              )}`}
                            >
                              {getConnectionType(topCompat.compatibility_score)}
                            </span>
                          </div>
                          <p className="text-divine-gold font-cinzel text-5xl">
                            {topCompat.compatibility_score}%
                          </p>
                        </div>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-divine-gold/30 bg-divine-gold/10 flex items-center justify-center flex-shrink-0 group-hover:border-divine-gold/60 transition-colors">
                            {(topCompat.thumbnail_url || topCompat.picture_url || topCompat.picture) ? (
                              <img
                                src={
                                  topCompat.thumbnail_url ||
                                  topCompat.picture_url ||
                                  topCompat.picture
                                }
                                alt={topCompat.name}
                                className="w-full h-full object-cover"
                                data-testid="top-compat-avatar"
                              />
                            ) : (
                              <span className="text-divine-gold font-cinzel text-xl">
                                {topCompat.name?.[0] || "?"}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-white font-medium truncate" data-testid="top-compat-name">
                              {topCompat.name}
                            </p>
                            {topCompat.summary && (
                              <p className="text-white/60 text-xs leading-snug line-clamp-2">
                                {topCompat.summary.replace(/<[^>]*>/g, "").substring(0, 140)}
                              </p>
                            )}
                          </div>
                        </div>
                        <Button
                          data-testid="view-top-compat-btn"
                          variant="outline"
                          size="sm"
                          className="w-full border-divine-gold text-divine-gold hover:bg-divine-gold/10 rounded-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/relationships/${topCompat.match_id}/compatibility`);
                          }}
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          View Compatibility
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-4" data-testid="top-compat-empty">
                        <Sparkles className="w-10 h-10 text-divine-gold/40 mx-auto mb-2" />
                        <p className="text-white/60 text-sm mb-3">
                          No compatibility scores yet. Analyze compatibility on any
                          relationship to see your top match here.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full border-divine-gold text-divine-gold hover:bg-divine-gold/10 rounded-full"
                          onClick={() => navigate("/relationships")}
                        >
                          <Heart className="w-4 h-4 mr-2" />
                          Browse Relationships
                        </Button>
                      </div>
                    )
                  ) : match?.compatibility_score ? (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-cinzel text-lg text-white">Compatibility</h3>
                          <span className={`text-sm font-semibold ${getConnectionColor(match.compatibility_score)}`}>
                            {getConnectionType(match.compatibility_score)}
                          </span>
                        </div>
                        <p className="text-divine-gold font-cinzel text-5xl">{match.compatibility_score}%</p>
                      </div>
                      {match.analysis?.summary && (
                        <p className="text-white/70 text-sm leading-relaxed line-clamp-2 mb-3">
                          {match.analysis.summary.replace(/<[^>]*>/g, '').substring(0, 120)}
                        </p>
                      )}
                      <Button
                        data-testid="view-compatibility-btn"
                        onClick={() => navigate(`/relationships/${matchId}/compatibility`)}
                        variant="outline"
                        size="sm"
                        className="w-full border-divine-gold text-divine-gold hover:bg-divine-gold/10 rounded-full"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        View Compatibility
                      </Button>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <Sparkles className="w-10 h-10 text-divine-gold/40 mx-auto mb-2" />
                      <p className="text-white/60 text-sm mb-3">No compatibility analysis yet</p>
                      {(() => {
                        const matchCompletion = match?.profile_completion ?? 0;
                        const dataGate = matchCompletion >= 90 && userProfileCompletion >= 90;
                        if (dataGate) {
                          return (
                            <Button
                              data-testid="analyze-compatibility-btn"
                              onClick={handleAnalyze}
                              disabled={analyzing}
                              variant="outline"
                              size="sm"
                              className="w-full border-divine-gold text-divine-gold hover:bg-divine-gold/10 rounded-full"
                            >
                              {analyzing ? (
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Sparkles className="w-4 h-4 mr-2" />
                              )}
                              {analyzing ? "Analyzing..." : "Analyze Compatibility"}
                            </Button>
                          );
                        }
                        return (
                          <Button
                            data-testid="insufficient-data-btn"
                            onClick={() => setInsufficientDataOpen(true)}
                            variant="outline"
                            size="sm"
                            className="w-full border-amber-500/40 text-amber-400 hover:bg-amber-500/10 rounded-full"
                          >
                            <AlertCircle className="w-4 h-4 mr-2" />
                            Insufficient Data for Analysis
                          </Button>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-cinzel text-lg text-white mb-4">Quick Actions</h3>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => navigate(`/eroscoach/${matchId}`)}
                    variant="outline"
                    className="border-divine-gold text-divine-gold hover:bg-divine-gold/10 rounded-full"
                  >
                    <span className="mr-2">🏹</span>
                    Agent Eros Advice
                  </Button>
                  
                  <Button
                    onClick={() => setRoleplayModalOpen(true)}
                    variant="outline"
                    className="border-divine-gold text-divine-gold hover:bg-divine-gold/10 rounded-full"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    {match?.name?.split(' ')[0] || 'Persona'} AI Roleplay
                  </Button>
                </div>
              </div>

              {/* Roleplay Beta Modal */}
              {roleplayModalOpen && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setRoleplayModalOpen(false)}>
                  <div className="bg-divine-black-light border border-white/10 rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
                    <div className="text-center mb-4">
                      <div className="w-12 h-12 bg-divine-gold/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <MessageCircle className="w-6 h-6 text-divine-gold" />
                      </div>
                      <h3 className="font-cinzel text-lg text-divine-gold">Beta Development</h3>
                    </div>
                    <p className="text-white/70 text-sm leading-relaxed mb-2">
                      We are currently working on this Beta feature to be released soon.
                    </p>
                    <p className="text-white/70 text-sm leading-relaxed mb-4">
                      For a lite Roleplay experience, in any coaching session you can ask Agent Eros to roleplay a response, enter full roleplay and chat. The dedicated AI Roleplay mode will be more advanced for realistic but simulated persona interactions.
                    </p>
                    <div className="flex gap-3">
                      <Button
                        data-testid="roleplay-modal-close"
                        onClick={() => setRoleplayModalOpen(false)}
                        variant="outline"
                        className={`flex-1 border-white/20 text-white/60 hover:text-white hover:bg-white/5 rounded-full ${isAdmin ? '' : 'w-full'}`}
                      >
                        Close
                      </Button>
                      {isAdmin && (
                        <Button
                          data-testid="roleplay-modal-admin"
                          onClick={() => { setRoleplayModalOpen(false); navigate(`/chat/${matchId}`); }}
                          className="flex-1 bg-divine-gold/20 text-divine-gold hover:bg-divine-gold/30 border border-divine-gold/30 rounded-full"
                        >
                          <Shield className="w-4 h-4 mr-2" />
                          Admin AI Roleplay
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Profile Data Sections */}
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="glass-gold rounded-xl p-6">
                  <SectionHeader title="Basic Information" sectionKey="basic" matchId={matchId} onEdit={gatedEdit} />
                  <div className="grid md:grid-cols-2 gap-4">
                    <FieldDisplay label="Name" fieldName="name" value={match?.name} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.name} onPrivacyChange={handlePrivacyChange} />
                    {!isUserPersona && (
                      <FieldDisplay label="Define Their Relationship With You" fieldName="relationship_status" value={match?.relationship_status?.replace(/_/g, " ")} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.relationship_status} onPrivacyChange={handlePrivacyChange} />
                    )}
                    {!isUserPersona && (
                    <div>
                      <label className="text-white/50 text-xs uppercase tracking-wider flex items-center gap-1.5">
                        {showPrivacyIcons && (
                          <PrivacyIcon
                            fieldKey="persona_connection"
                            privacyLevel={privacySettings?.persona_connection || 0}
                            matchId={matchId}
                            matchName={match?.name}
                            onPrivacyChange={handlePrivacyChange}
                            size="sm"
                          />
                        )}
                        Persona Connection Level
                        <button
                          onClick={() => setShowStagesHelp(true)}
                          className="text-white/40 hover:text-divine-gold transition-colors"
                          title="View all 13 stages"
                        >
                          <HelpCircle className="w-4 h-4" />
                        </button>
                        {showFieldNames && <span className="ml-2 text-divine-gold font-mono bg-divine-gold/10 px-1 rounded normal-case">persona_connection</span>}
                      </label>
                      <p className="text-white mt-1">
                        L{match?.persona_connection || 1}: {PERSONA_CONNECTION_LEVELS.find(l => l.value === (match?.persona_connection || 1))?.title || "Spark"}
                      </p>
                      <p className="text-white/50 text-xs mt-0.5">
                        {PERSONA_CONNECTION_LEVELS.find(l => l.value === (match?.persona_connection || 1))?.description}
                      </p>
                    </div>
                    )}
                  </div>
                  {!isUserPersona && match?.user_match_goal && (
                    <div className="mt-4">
                      <FieldDisplay 
                        label="What is your target goal for this relationship?" 
                        fieldName="user_match_goal" 
                        value={match?.user_match_goal} 
                        showDebug={showFieldNames} 
                        showPrivacy={false}
                        matchId={matchId} 
                        matchName={match?.name}
                      />
                    </div>
                  )}
                </div>

                {/* Identity */}
                <div className="glass-gold rounded-xl p-6">
                  <SectionHeader title="Personal Identity" sectionKey="identity" matchId={matchId} onEdit={gatedEdit} />
                  <div className="grid md:grid-cols-4 gap-4">
                    <FieldDisplay label="Sex" fieldName="sex" value={match?.sex?.replace(/_/g, " ")} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.sex} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Pronouns" fieldName="pronouns" value={match?.pronouns?.replace(/_/g, "/")} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.pronouns} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Sexual Orientation" fieldName="sexual_orientation" value={match?.sexual_orientation?.replace(/_/g, " ")} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.sexual_orientation} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Ethnicity" fieldName="ethnicity" value={match?.ethnicity} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.ethnicity} onPrivacyChange={handlePrivacyChange} />
                  </div>
                </div>

                {/* Physical Appearance - NEW */}
                <div className="glass-gold rounded-xl p-6">
                  <SectionHeader title="Physical Appearance" sectionKey="appearance" matchId={matchId} onEdit={gatedEdit} />
                  <FieldDisplay label="Vibe / Style / Presentation" fieldName="vibe_style_presentation" value={match?.vibe_style_presentation} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.vibe_style_presentation} onPrivacyChange={handlePrivacyChange} className="mb-4" />
                  <div className="grid md:grid-cols-3 gap-4">
                    <FieldDisplay label="Height" fieldName="height" value={match?.height} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.height} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Weight" fieldName="weight" value={match?.weight} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.weight} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Body Type" fieldName="body_type" value={BODY_TYPE_LABELS[match?.body_type] || match?.body_type} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.body_type} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 mt-4">
                    <FieldDisplay label="Hair Color" fieldName="hair_color" value={match?.hair_color} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.hair_color} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Eye Color" fieldName="eye_color" value={match?.eye_color} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.eye_color} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Complexion" fieldName="complexion" value={match?.complexion} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.complexion} onPrivacyChange={handlePrivacyChange} />
                  </div>
                </div>

                {/* Demographics & Location */}
                <div className="glass-gold rounded-xl p-6">
                  <SectionHeader title="Demographics & Location" sectionKey="demographics" matchId={matchId} onEdit={gatedEdit} />
                  <div className="grid md:grid-cols-4 gap-4">
                    <FieldDisplay label="Living Location" fieldName="living_location" value={match?.living_location} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.living_location} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Birth Location" fieldName="birth_location" value={match?.birth_location} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.birth_location} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Age" fieldName="age" value={match?.age} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.age} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Date of Birth" fieldName="date_of_birth" value={match?.date_of_birth} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.date_of_birth} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <FieldDisplay label="Time of Birth" fieldName="time_of_birth" value={match?.time_of_birth} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.time_of_birth} onPrivacyChange={handlePrivacyChange} />
                    {(match?.date_of_birth || match?.zodiac_sign || match?.chinese_zodiac) && (
                      <div>
                        <div className="flex items-center gap-2">
                          <label className="text-white/50 text-xs uppercase tracking-wider">Zodiac</label>
                          {showFieldNames && (
                            <span className="text-divine-gold font-mono bg-divine-gold/10 px-1 rounded text-xs">zodiac_sign, chinese_zodiac</span>
                          )}
                        </div>
                        <p className="text-divine-gold mt-1">
                          {(match?.zodiac_sign || getZodiacSign(match?.date_of_birth))} • {(match?.chinese_zodiac || getChineseZodiac(match?.date_of_birth))}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Relationship Data (renamed from Relationship Context) */}
                <div className="glass-gold rounded-xl p-6">
                  <SectionHeader title="Relationship Data" sectionKey="relationship" matchId={matchId} onEdit={gatedEdit} />
                  <div className="grid md:grid-cols-2 gap-4">
                    <FieldDisplay label="Their Relationship Status" fieldName="their_relationship_status" value={findLabel(THEIR_REL_STATUS_OPTIONS, match?.their_relationship_status)} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.their_relationship_status} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay 
                      label="Relationship Type" 
                      fieldName="relationship_type" 
                      value={findLabel(REL_TYPE_OPTIONS, match?.relationship_type)} 
                      showDebug={showFieldNames} 
                      showPrivacy={showPrivacyIcons} 
                      matchId={matchId} 
                      matchName={match?.name} 
                      privacyLevel={privacySettings?.relationship_type} 
                      onPrivacyChange={handlePrivacyChange}
                      descriptor={REL_TYPE_OPTIONS.find(opt => opt.value === match?.relationship_type)?.descriptor}
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <FieldDisplay label="Their Current Partner" fieldName="their_current_partner" value={match?.their_current_partner} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.their_current_partner} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Their Ex Partners" fieldName="their_ex_partner_details" value={match?.their_ex_partner_details} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.their_ex_partner_details} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <FieldDisplay label="Libido / Intimacy" fieldName="libido_intimacy" value={match?.libido_intimacy} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.libido_intimacy} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Fetishes" fieldName="fetishes" value={match?.fetishes} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.fetishes} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  
                  {/* Separator line */}
                  <div className="border-t border-white/10 my-6" />
                  
                  {!isUserPersona && (
                  <div className="mt-4">
                    <FieldDisplay label="History Together" fieldName="history_together" value={match?.history_together} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.history_together} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  )}
                  {!isUserPersona && (
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <FieldDisplay label="Their Feelings Toward You" fieldName="their_feelings_toward_you" value={match?.their_feelings_toward_you} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.their_feelings_toward_you} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Your Feelings Toward Them" fieldName="your_feelings_toward_them" value={match?.your_feelings_toward_them} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.your_feelings_toward_them} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  )}
                  {/* Love Language & Attachment moved here */}
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <FieldDisplay label="Primary Love Language" fieldName="primary_love_lang" value={match?.primary_love_lang?.replace(/_/g, " ")} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.primary_love_lang} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Secondary Love Language" fieldName="secondary_love_lang" value={match?.secondary_love_lang?.replace(/_/g, " ")} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.secondary_love_lang} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  <div className="mt-4">
                    <label className="text-white/50 text-xs uppercase tracking-wider">
                      Attachment Style
                      {showFieldNames && <span className="ml-2 text-divine-gold font-mono bg-divine-gold/10 px-1 rounded normal-case">attachment_style</span>}
                    </label>
                    {match?.attachment_style ? (
                      <div className="mt-1">
                        <p className="text-white capitalize">{ATTACHMENT_STYLES.find(s => s.value === match.attachment_style)?.label || match.attachment_style.replace(/_/g, " ")}</p>
                        <p className="text-white/60 text-sm italic mt-1">{ATTACHMENT_STYLES.find(s => s.value === match.attachment_style)?.description}</p>
                      </div>
                    ) : (
                      <p className="text-white mt-1">-</p>
                    )}
                  </div>
                  {/* New Relationship Data fields */}
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <FieldDisplay label="Relationship Goals" fieldName="relationship_goals" value={match?.relationship_goals} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.relationship_goals} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Boundaries" fieldName="boundaries" value={match?.boundaries} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.boundaries} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <FieldDisplay label="Seeking / Looking For" fieldName="seeking_looking_for" value={match?.seeking_looking_for} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.seeking_looking_for} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Turn Offs" fieldName="turn_offs" value={match?.turn_offs} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.turn_offs} onPrivacyChange={handlePrivacyChange} />
                  </div>
                </div>

                {/* Core Personality Signals */}
                <div className="glass-gold rounded-xl p-6">
                  <SectionHeader title="Core Personality Signals" sectionKey="personality" matchId={matchId} onEdit={gatedEdit} />
                  
                  {/* Identity & Orientation */}
                  <div className="mb-6">
                    <h4 className="text-white/60 text-sm font-medium border-b border-white/10 pb-2 mb-3">Identity & Orientation</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <FieldDisplay label="Self-described personality" fieldName="self_personality_words" value={match?.self_personality_words} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.self_personality_words} onPrivacyChange={handlePrivacyChange} />
                      <FieldDisplay label="What people often misunderstand" fieldName="misunderstandings" value={match?.misunderstandings} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.misunderstandings} onPrivacyChange={handlePrivacyChange} />
                    </div>
                      <div className="mt-4">
                        <MultiSelectDisplay label="What matters most in relationships" fieldName="relationship_matters" values={match?.relationship_matters} showDebug={showFieldNames} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.relationship_matters} onPrivacyChange={handlePrivacyChange} showPrivacy={showPrivacyIcons} />
                      </div>
                      <div className="mt-4">
                        <FieldDisplay label="Top 3 deal-breakers" fieldName="top_dealbreakers" value={match?.top_dealbreakers} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.top_dealbreakers} onPrivacyChange={handlePrivacyChange} />
                      </div>
                    </div>

                    {/* Emotional Orientation */}
                    <div className="mb-6">
                      <h4 className="text-white/60 text-sm font-medium border-b border-white/10 pb-2 mb-3">Emotional Orientation</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <FieldDisplay label="When stressed, I tend to:" fieldName="stress_response" value={getDropdownLabel("stress_response", match?.stress_response)} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.stress_response} onPrivacyChange={handlePrivacyChange} />
                        <FieldDisplay label="I feel most connected when:" fieldName="connection_style" value={getDropdownLabel("connection_style", match?.connection_style)} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.connection_style} onPrivacyChange={handlePrivacyChange} />
                      </div>
                    </div>

                    {/* Communication Preferences */}
                    <div className="mb-6">
                      <h4 className="text-white/60 text-sm font-medium border-b border-white/10 pb-2 mb-3">Communication Preferences</h4>
                      <div className="grid md:grid-cols-3 gap-4">
                        <FieldDisplay label="Preferred communication style" fieldName="communication_pref_style" value={getDropdownLabel("communication_pref_style", match?.communication_pref_style)} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.communication_pref_style} onPrivacyChange={handlePrivacyChange} />
                        <FieldDisplay label="Message length preference" fieldName="message_length_pref" value={getDropdownLabel("message_length_pref", match?.message_length_pref)} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.message_length_pref} onPrivacyChange={handlePrivacyChange} />
                        <FieldDisplay label="Conflict style" fieldName="conflict_style" value={getDropdownLabel("conflict_style", match?.conflict_style)} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.conflict_style} onPrivacyChange={handlePrivacyChange} />
                      </div>
                    </div>

                    {/* Attachment & Pace */}
                    <div className="mb-6">
                      <h4 className="text-white/60 text-sm font-medium border-b border-white/10 pb-2 mb-3">Attachment & Pace</h4>
                      <div className="grid md:grid-cols-3 gap-4">
                        <FieldDisplay label="I open up emotionally:" fieldName="emotional_openness" value={getDropdownLabel("emotional_openness", match?.emotional_openness)} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.emotional_openness} onPrivacyChange={handlePrivacyChange} />
                        <FieldDisplay label="I need consistency to feel safe:" fieldName="needs_consistency" value={getDropdownLabel("needs_consistency", match?.needs_consistency)} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.needs_consistency} onPrivacyChange={handlePrivacyChange} />
                        <FieldDisplay label="I prefer relationships that feel:" fieldName="relationship_feel_pref" value={getDropdownLabel("relationship_feel_pref", match?.relationship_feel_pref)} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.relationship_feel_pref} onPrivacyChange={handlePrivacyChange} />
                      </div>
                    </div>

                    {/* Boundaries & Sensitivities */}
                    <div className="mb-6">
                      <h4 className="text-white/60 text-sm font-medium border-b border-white/10 pb-2 mb-3">Boundaries & Sensitivities</h4>
                      <MultiSelectDisplay label="Things that shut me down quickly" fieldName="shutdown_triggers" values={match?.shutdown_triggers} showDebug={showFieldNames} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.shutdown_triggers} onPrivacyChange={handlePrivacyChange} showPrivacy={showPrivacyIcons} />
                      {match?.shutdown_other && (
                        <div className="mt-4">
                          <FieldDisplay label="Other shutdown triggers" fieldName="shutdown_other" value={match?.shutdown_other} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.shutdown_other} onPrivacyChange={handlePrivacyChange} />
                        </div>
                      )}
                      <div className="grid md:grid-cols-2 gap-4 mt-4">
                        <FieldDisplay label="Topics I don't like early" fieldName="topics_avoid_early" value={match?.topics_avoid_early} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.topics_avoid_early} onPrivacyChange={handlePrivacyChange} />
                        <FieldDisplay label="What respect looks like to me" fieldName="respect_meaning" value={match?.respect_meaning} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.respect_meaning} onPrivacyChange={handlePrivacyChange} />
                      </div>
                    </div>

                    {/* Energy & Rhythm */}
                    <div className="mb-6">
                      <h4 className="text-white/60 text-sm font-medium border-b border-white/10 pb-2 mb-3">Energy & Rhythm</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <FieldDisplay label="Social energy" fieldName="social_energy" value={getDropdownLabel("social_energy", match?.social_energy)} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.social_energy} onPrivacyChange={handlePrivacyChange} />
                        <FieldDisplay label="Ideal connection rhythm" fieldName="connection_rhythm" value={getDropdownLabel("connection_rhythm", match?.connection_rhythm)} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.connection_rhythm} onPrivacyChange={handlePrivacyChange} />
                      </div>
                    </div>

                    {/* Attraction & Desire */}
                    <div className="mb-6">
                      <h4 className="text-white/60 text-sm font-medium border-b border-white/10 pb-2 mb-3">Attraction & Desire</h4>
                      <MultiSelectDisplay label="I feel attraction when someone is:" fieldName="attraction_types" values={match?.attraction_types} showDebug={showFieldNames} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.attraction_types} onPrivacyChange={handlePrivacyChange} showPrivacy={showPrivacyIcons} />
                      <div className="mt-4">
                        <FieldDisplay label="Flirting style" fieldName="flirting_style" value={getDropdownLabel("flirting_style", match?.flirting_style)} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.flirting_style} onPrivacyChange={handlePrivacyChange} />
                      </div>
                    </div>

                    {/* Sensory & Lifestyle */}
                    <div>
                      <h4 className="text-white/60 text-sm font-medium border-b border-white/10 pb-2 mb-3">Sensory & Lifestyle</h4>
                      <FieldDisplay label="Activities that light me up" fieldName="activities_light_up" value={match?.activities_light_up} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.activities_light_up} onPrivacyChange={handlePrivacyChange} />
                      <div className="grid md:grid-cols-2 gap-4 mt-4">
                        <FieldDisplay label="How important is physical touch? (1-10)" fieldName="physical_touch_importance" value={match?.physical_touch_importance} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.physical_touch_importance} onPrivacyChange={handlePrivacyChange} />
                        <FieldDisplay label="Preferred date vibe" fieldName="date_vibe_pref" value={getDropdownLabel("date_vibe_pref", match?.date_vibe_pref)} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.date_vibe_pref} onPrivacyChange={handlePrivacyChange} />
                      </div>
                    </div>
                  </div>

                {/* Work & Career */}
                <div className="glass-gold rounded-xl p-6">
                  <SectionHeader title="Work & Career" sectionKey="work" matchId={matchId} onEdit={gatedEdit} />
                  <div className="space-y-4">
                    <FieldDisplay label="Current Work" fieldName="work_details" value={match?.work_details} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.work_details} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Work Ambitions & Goals" fieldName="work_ambition_goals" value={match?.work_ambition_goals} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.work_ambition_goals} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Work History" fieldName="work_history" value={match?.work_history} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.work_history} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Education History" fieldName="education_history" value={match?.education_history} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.education_history} onPrivacyChange={handlePrivacyChange} />
                  </div>
                </div>

                {/* Lifestyle & Interests */}
                <div className="glass-gold rounded-xl p-6">
                  <SectionHeader title="Lifestyle & Interests" sectionKey="lifestyle" matchId={matchId} onEdit={gatedEdit} />
                  <FieldDisplay label="Hobbies & Passions" fieldName="hobbies_passions" value={match?.hobbies_passions} showDebug={showFieldNames} className="mb-4" />
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <FieldDisplay label="Favourite Music" fieldName="favourite_music" value={match?.favourite_music} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.favourite_music} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Favourite Movies/Series" fieldName="favourite_movies_series" value={match?.favourite_movies_series} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.favourite_movies_series} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <FieldDisplay label="Favourite Alt Media/Blogs" fieldName="favourite_alt_media_blogs" value={match?.favourite_alt_media_blogs} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.favourite_alt_media_blogs} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Favourite Books/Audio" fieldName="favourite_books_audio" value={match?.favourite_books_audio} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.favourite_books_audio} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <FieldDisplay label="Smoking / Vape" fieldName="smoking_vape" value={match?.smoking_vape} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.smoking_vape} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Drinks Alcohol" fieldName="drinks_alcohol" value={match?.drinks_alcohol} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.drinks_alcohol} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <FieldDisplay label="Car / Drive Type" fieldName="car_drive_type" value={match?.car_drive_type} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.car_drive_type} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Travel History" fieldName="travel_history" value={match?.travel_history} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.travel_history} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <FieldDisplay label="Lived History" fieldName="lived_history" value={match?.lived_history} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.lived_history} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Skills & Talents" fieldName="skills_talents" value={match?.skills_talents} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.skills_talents} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <FieldDisplay label="Good Habits / Self-Care" fieldName="good_habits_self_care" value={match?.good_habits_self_care} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.good_habits_self_care} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Bad Habits" fieldName="bad_habits" value={match?.bad_habits} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.bad_habits} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <FieldDisplay label="Favourite Food & Drink" fieldName="favourite_food_drink" value={match?.favourite_food_drink} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.favourite_food_drink} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Fears / No-Go" fieldName="fears_no_go" value={match?.fears_no_go} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.fears_no_go} onPrivacyChange={handlePrivacyChange} />
                  </div>
                </div>

                {/* Family */}
                <div className="glass-gold rounded-xl p-6">
                  <SectionHeader title="Family" sectionKey="family" matchId={matchId} onEdit={gatedEdit} />
                  <div className="grid md:grid-cols-2 gap-4">
                    <FieldDisplay label="Parents/Grandparents" fieldName="parent_details" value={match?.parent_details} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.parent_details} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Siblings" fieldName="sibling_details" value={match?.sibling_details} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.sibling_details} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <FieldDisplay label="Children" fieldName="children_details" value={match?.children_details} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.children_details} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Pets" fieldName="pets_details" value={match?.pets_details} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.pets_details} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  <div className="mt-4">
                    <FieldDisplay label="Family Style / Type" fieldName="family_style_type_details" value={match?.family_style_type_details} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.family_style_type_details} onPrivacyChange={handlePrivacyChange} />
                  </div>
                </div>

                {/* Attractions & Dreams */}
                <div className="glass-gold rounded-xl p-6">
                  <SectionHeader title="Attractions & Dreams" sectionKey="attractions" matchId={matchId} onEdit={gatedEdit} />
                  <div className="space-y-4">
                    <FieldDisplay label="Physically attracted to" fieldName="physical_attraction_type" value={match?.physical_attraction_type} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.physical_attraction_type} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Fantasies & Dreams" fieldName="fantasies_dreams" value={match?.fantasies_dreams} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.fantasies_dreams} onPrivacyChange={handlePrivacyChange} />
                  </div>
                </div>

                {/* Social Dynamics */}
                <div className="glass-gold rounded-xl p-6">
                  <SectionHeader title="Social Dynamics" sectionKey="social" matchId={matchId} onEdit={gatedEdit} />
                  <FieldDisplay label="Friends" fieldName="friends" value={match?.friends} showDebug={showFieldNames} className="mb-4" />
                  <div className="grid md:grid-cols-2 gap-4">
                    <FieldDisplay label="People They Admire" fieldName="people_admire" value={match?.people_admire} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.people_admire} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="People They Dislike" fieldName="people_dislike" value={match?.people_dislike} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.people_dislike} onPrivacyChange={handlePrivacyChange} />
                  </div>
                </div>

                {/* Values */}
                <div className="glass-gold rounded-xl p-6">
                  <SectionHeader title="Values" sectionKey="values" matchId={matchId} onEdit={gatedEdit} />
                  <div className="grid md:grid-cols-2 gap-4">
                    <FieldDisplay label="Political Views" fieldName="political_views" value={match?.political_views} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.political_views} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Spiritual Views" fieldName="spiritual_views" value={match?.spiritual_views} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.spiritual_views} onPrivacyChange={handlePrivacyChange} />
                  </div>
                  <div className="mt-4">
                    <FieldDisplay label="Religion" fieldName="religion" value={match?.religion} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.religion} onPrivacyChange={handlePrivacyChange} />
                  </div>
                </div>

                {/* Communication Style */}
                <div className="glass-gold rounded-xl p-6">
                  <SectionHeader title="Communication Style" sectionKey="communication" matchId={matchId} onEdit={gatedEdit} />
                  <div className="grid md:grid-cols-2 gap-4">
                    <FieldDisplay label="Communication Style" fieldName="communication_style" value={match?.communication_style} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.communication_style} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Texting Style" fieldName="texting_style" value={match?.texting_style} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.texting_style} onPrivacyChange={handlePrivacyChange} />
                  </div>
                </div>

                {/* Physical & Mental Health */}
                <div className="glass-gold rounded-xl p-6">
                  <SectionHeader title="Physical & Mental Health" sectionKey="health" matchId={matchId} onEdit={gatedEdit} />
                  <div className="space-y-4">
                    <FieldDisplay label="Known Mental Health Diagnosis" fieldName="known_mental_health_diagnosis" value={Array.isArray(match?.known_mental_health_diagnosis) ? match.known_mental_health_diagnosis.join(", ") : match?.known_mental_health_diagnosis} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.known_mental_health_diagnosis} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Possible Mental Health Issues" fieldName="possible_mental_health_issues" value={Array.isArray(match?.possible_mental_health_issues) ? match.possible_mental_health_issues.join(", ") : match?.possible_mental_health_issues} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.possible_mental_health_issues} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Physical Health Issues" fieldName="physical_health_issues" value={Array.isArray(match?.physical_health_issues) ? match.physical_health_issues.join(", ") : match?.physical_health_issues} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.physical_health_issues} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Medications Being Taken" fieldName="medications_being_taken" value={Array.isArray(match?.medications_being_taken) ? match.medications_being_taken.join(", ") : match?.medications_being_taken} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.medications_being_taken} onPrivacyChange={handlePrivacyChange} />
                  </div>
                </div>

                {/* Bio & Online Presence */}
                <div className="glass-gold rounded-xl p-6">
                  <SectionHeader title="Bio & Online Presence" sectionKey="bio" matchId={matchId} onEdit={gatedEdit} />
                  <div className="space-y-4">
                    <FieldDisplay label="Bio" fieldName="bio" value={match?.bio} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.bio} onPrivacyChange={handlePrivacyChange} />
                    <FieldDisplay label="Notes" fieldName="notes" value={match?.notes} showDebug={showFieldNames} showPrivacy={showPrivacyIcons} matchId={matchId} matchName={match?.name} privacyLevel={privacySettings?.notes} onPrivacyChange={handlePrivacyChange} />
                    {match?.pasted_data && (
                      <div>
                        <label className="text-white/50 text-xs uppercase tracking-wider">
                          Raw Pasted Data
                          {showFieldNames && <span className="ml-2 text-divine-gold font-mono bg-divine-gold/10 px-1 rounded normal-case">pasted_data</span>}
                        </label>
                        <p className="text-white/60 mt-1 whitespace-pre-wrap text-sm max-h-[200px] overflow-y-auto bg-white/5 p-3 rounded-lg">{match.pasted_data}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline" className="mt-6">
            <div className="max-w-4xl mx-auto">
              <TimelineTab matchId={matchId} matchName={match?.name || "them"} />
            </div>
          </TabsContent>

          {/* Images Tab */}
          <TabsContent value="images" className="mt-6">
            <div className="max-w-4xl mx-auto">
              <div className="glass-gold rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-cinzel text-lg text-white flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-divine-gold" />
                    Photos & Videos of {match?.name}
                  </h3>
                  
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*,video/mp4,video/webm,video/quicktime"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      uploadingImage 
                        ? "bg-white/10 text-white/50 cursor-not-allowed" 
                        : "bg-divine-gold/20 hover:bg-divine-gold/30 text-divine-gold"
                    }`}>
                      {uploadingImage ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      <span className="text-sm font-medium">
                        {uploadingImage ? "Uploading..." : "Upload Photo"}
                      </span>
                    </div>
                  </label>
                </div>

                {loadingImages ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-divine-gold animate-spin" />
                  </div>
                ) : images.length === 0 ? (
                  <div className="text-center py-12">
                    <ImageIcon className="w-16 h-16 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50 mb-2">No photos or videos yet</p>
                    <p className="text-white/30 text-sm">Upload photos or videos of {match?.name} to personalize their profile</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {images.map((image) => {
                      // Support both old (data) and new (src/file_path) formats
                      const mediaSrc = getMediaUrl(image.src || image.file_path || image.data || "");
                      const thumbnailSrc = getMediaUrl(image.thumbnail || "");
                      const isVideo = image.media_type === "video" || mediaSrc.includes("video");
                      const displaySrc = isVideo ? (thumbnailSrc || mediaSrc) : mediaSrc;
                      // A media item is broken when it has no src at all,
                      // OR when the <img> onError handler previously flagged
                      // its image_id (see markMediaBroken).
                      const isBroken = (!mediaSrc && !thumbnailSrc) || brokenMediaIds.has(image.image_id);
                      
                      return (
                        <div
                          key={image.image_id}
                          className={`relative rounded-lg overflow-hidden border-2 ${
                            image.is_primary ? "border-divine-gold" : isBroken ? "border-red-500/50" : "border-white/20"
                          }`}
                        >
                          {/* Image/Video Thumbnail */}
                          {isBroken ? (
                            <div className="w-full aspect-square bg-red-900/20 flex items-center justify-center" data-testid={`media-broken-${image.image_id}`}>
                              <div className="text-center p-4">
                                <ImageIcon className="w-8 h-8 text-red-400 mx-auto mb-2" />
                                <p className="text-red-400 text-xs">{isVideo ? "Video unavailable" : "Image unavailable"}</p>
                                <p className="text-white/40 text-xs mt-1">Re-upload required</p>
                              </div>
                            </div>
                          ) : isVideo ? (
                            <img
                              src={displaySrc}
                              alt={image.caption || `Video of ${match?.name}`}
                              className="w-full aspect-square object-cover"
                              onError={() => markMediaBroken(image.image_id)}
                            />
                          ) : (
                            <img
                              src={mediaSrc}
                              alt={image.caption || `Photo of ${match?.name}`}
                              className="w-full aspect-square object-cover cursor-pointer"
                              onClick={() => setSelectedImageForView({ ...image, mediaSrc })}
                              onError={() => markMediaBroken(image.image_id)}
                            />
                          )}
                          
                          {/* Top Left - Star Button: Primary for images, Chat playlist for videos */}
                          {isVideo ? (
                            // Video: Star toggles "show in chat" playlist
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleVideoInChat(image.image_id);
                              }}
                              className={`absolute top-2 left-2 p-1.5 rounded-full transition-colors z-20 ${
                                image.show_in_chat 
                                  ? "bg-purple-500 text-white" 
                                  : "bg-black/60 text-white/70 hover:bg-purple-500/80 hover:text-white"
                              }`}
                              title={image.show_in_chat ? "Playing in chat (click to remove)" : "Add to chat playlist"}
                            >
                              <Star className={`w-4 h-4 ${image.show_in_chat ? "fill-current" : ""}`} />
                            </button>
                          ) : (
                            // Image: Star sets as primary profile picture
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!image.is_primary) handleSetPrimaryImage(image.image_id);
                              }}
                              className={`absolute top-2 left-2 p-1.5 rounded-full transition-colors z-20 ${
                                image.is_primary 
                                  ? "bg-divine-gold text-black cursor-default" 
                                  : "bg-black/60 text-white/70 hover:bg-divine-gold/80 hover:text-black"
                              }`}
                              title={image.is_primary ? "Primary profile picture" : "Set as primary"}
                              disabled={image.is_primary}
                            >
                              <Star className={`w-4 h-4 ${image.is_primary ? "fill-current" : ""}`} />
                            </button>
                          )}
                          
                          {/* Video Badge - Top Right */}
                          {isVideo && (
                            <div className="absolute top-2 right-2 px-2 py-1 bg-purple-500/90 text-white text-xs font-medium rounded z-20">
                              VIDEO
                            </div>
                          )}
                          
                          {/* Center Play Button for Videos */}
                          {isVideo && (
                            <div 
                              className="absolute inset-0 flex items-center justify-center cursor-pointer z-10"
                              onClick={() => setSelectedImageForView({ ...image, mediaSrc })}
                            >
                              <div className="w-14 h-14 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center transition-colors">
                                <Play className="w-7 h-7 text-white ml-1" />
                              </div>
                            </div>
                          )}
                          
                          {/* Bottom Right - Delete Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteImage(image.image_id);
                            }}
                            className="absolute bottom-2 right-2 p-1.5 bg-black/60 hover:bg-red-500 text-white/70 hover:text-white rounded-full transition-colors z-20"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                <p className="text-white/40 text-xs mt-4 text-center">
                  The primary photo/video will be used as {match?.name}&apos;s profile picture (videos use first frame)
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Data Sources Tab */}
          <TabsContent value="data-sources" className="mt-6">
            <DataSourcesManager 
              ownerType="relationship" 
              ownerId={matchId} 
              ownerName={match?.name}
            />
          </TabsContent>

          {/* Learning Resources Tab */}
          <TabsContent value="learning" className="mt-6">
            <LearningResourcesManager 
              key={learningRefreshKey}
              ownerType="relationship" 
              ownerId={matchId} 
              ownerName={match?.name}
              isAdmin={isAdmin}
            />
          </TabsContent>

          {/* Memory Tab (formerly Familiarity) */}
          <TabsContent value="memory" className="mt-6">
            <FamiliarityV2 
              matchId={matchId} 
              matchName={match?.name}
              onBack={() => setActiveTab("overview")}
              showFieldNames={showFieldNames}
              userTimezone={user?.timezone}
            />
          </TabsContent>

          {/* Updates Tab — read-only history of claims + bio updates */}
          <TabsContent value="updates" className="mt-6">
            <UpdatesTab matchId={matchId} />
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Connection Stages Help Modal */}
      <Dialog open={showStagesHelp} onOpenChange={setShowStagesHelp}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Heart className="w-5 h-5 text-divine-gold" />
              13 Connection Stages
            </DialogTitle>
            <p className="text-white/50 text-sm">
              How relationships evolve and what AgentEros guides at each level
            </p>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-2 space-y-2 mt-4">
            {PERSONA_CONNECTION_LEVELS.map((level) => {
              const policy = STAGE_POLICIES[level.value];
              const isExpanded = expandedStage === level.value;
              const isCurrentStage = match?.persona_connection === level.value;
              
              return (
                <div 
                  key={level.value}
                  className={`rounded-lg border transition-colors ${
                    isCurrentStage 
                      ? "border-divine-gold/50 bg-divine-gold/10" 
                      : "border-white/10 bg-white/5"
                  }`}
                >
                  {/* Stage Header - Clickable */}
                  <button
                    onClick={() => setExpandedStage(isExpanded ? null : level.value)}
                    className="w-full p-3 flex items-center gap-3 text-left hover:bg-white/5 transition-colors rounded-lg"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      isCurrentStage 
                        ? "bg-divine-gold text-black" 
                        : "bg-white/10 text-white/70"
                    }`}>
                      {level.value}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{level.title}</span>
                        {isCurrentStage && (
                          <span className="text-xs bg-divine-gold/30 text-divine-gold px-2 py-0.5 rounded">
                            Current
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-white/50">{level.description}</p>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-white/40" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-white/40" />
                    )}
                  </button>
                  
                  {/* Expanded Content */}
                  {isExpanded && policy && (
                    <div className="px-4 pb-4 space-y-4 border-t border-white/10 pt-4 ml-11">
                      {/* Objective */}
                      <div>
                        <h4 className="text-xs uppercase tracking-wider text-divine-gold mb-1">AgentEros Objective</h4>
                        <p className="text-sm text-white/80">{policy.objective}</p>
                      </div>
                      
                      {/* Persona Perspective */}
                      <div>
                        <h4 className="text-xs uppercase tracking-wider text-purple-400 mb-1">Persona Sees</h4>
                        <p className="text-sm text-white/70">{policy.personaSees}</p>
                      </div>
                      
                      {/* Style Meters */}
                      <div>
                        <h4 className="text-xs uppercase tracking-wider text-blue-400 mb-2">Communication Style</h4>
                        <div className="grid grid-cols-3 gap-3 text-xs">
                          <div>
                            <span className="text-white/50">Warmth</span>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="flex-1 h-1.5 bg-white/10 rounded-full">
                                <div className="h-full bg-pink-400 rounded-full" style={{width: `${policy.style.warmth}%`}} />
                              </div>
                              <span className="text-white/70 w-8">{policy.style.warmth}%</span>
                            </div>
                          </div>
                          <div>
                            <span className="text-white/50">Flirt</span>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="flex-1 h-1.5 bg-white/10 rounded-full">
                                <div className="h-full bg-red-400 rounded-full" style={{width: `${policy.style.flirt}%`}} />
                              </div>
                              <span className="text-white/70 w-8">{policy.style.flirt}%</span>
                            </div>
                          </div>
                          <div>
                            <span className="text-white/50">Disclosure</span>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="flex-1 h-1.5 bg-white/10 rounded-full">
                                <div className="h-full bg-amber-400 rounded-full" style={{width: `${policy.style.disclosure}%`}} />
                              </div>
                              <span className="text-white/70 w-8">{policy.style.disclosure}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Allowed / Disallowed */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-xs uppercase tracking-wider text-green-400 mb-2">AgentEros Allows</h4>
                          <ul className="space-y-1">
                            {policy.allowed.map((item, i) => (
                              <li key={i} className="text-xs text-white/70 flex items-start gap-1">
                                <span className="text-green-400">✓</span> {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="text-xs uppercase tracking-wider text-red-400 mb-2">AgentEros Avoids</h4>
                          <ul className="space-y-1">
                            {policy.disallowed.map((item, i) => (
                              <li key={i} className="text-xs text-white/70 flex items-start gap-1">
                                <span className="text-red-400">✗</span> {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      
                      {/* Boundaries */}
                      <div>
                        <h4 className="text-xs uppercase tracking-wider text-white/50 mb-1">Boundaries</h4>
                        <p className="text-xs text-white/60">{policy.boundaries.join(" • ")}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MatchDetail;
